
import { useMemo } from 'react';
import { subDays, isWithinInterval, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subYears } from 'date-fns';
import { RevenueStats, Payment } from '@/components/revenue/types';
import { PaymentType, DateRange } from '@/components/revenue/RevenueFilters';
import { trackSystemSettingChange } from '@/services/db/admin/adminActionTracking';

export const useRevenueCalculations = (
  students: any[],
  teachers: any[],
  paymentType: PaymentType,
  dateRange: DateRange,
  searchQuery: string,
  customDate?: Date
) => {
  const calculateRevenueStats = useMemo((): RevenueStats => {
    let totalRevenue = 0;
    let paidAmount = 0;
    let pendingAmount = 0;
    let partialAmount = 0;
    let totalTeacherSalaries = 0;

    students.forEach(student => {
      student.programs.forEach(program => {
        const fee = program.fee || 0;
        totalRevenue += fee;
        
        if (program.feeStatus === 'paid') {
          paidAmount += fee;
        } else if (program.feeStatus === 'pending') {
          pendingAmount += fee;
        } else if (program.feeStatus === 'partial') {
          partialAmount += fee;
        }
      });
    });
    
    teachers.forEach(teacher => {
      if (teacher.payments) {
        teacher.payments.forEach((payment: any) => {
          totalTeacherSalaries += payment.amount;
        });
      } else if (teacher.salary) {
        totalTeacherSalaries += parseFloat(teacher.salary) || 0;
      }
    });

    try {
      trackSystemSettingChange(
        'revenue_calculation', 
        'system',
        null,
        {
          totalRevenue,
          paidAmount,
          pendingAmount,
          partialAmount,
          teacherSalaries: totalTeacherSalaries,
          calculatedAt: new Date().toISOString()
        }
      );
    } catch (error) {
      console.error('Error tracking revenue calculation:', error);
    }

    // Return real values only - no fallback examples
    return {
      totalRevenue,
      paidAmount: paidAmount,
      pendingAmount: pendingAmount,
      partialAmount: partialAmount,
      teacherSalaries: totalTeacherSalaries
    };
  }, [students, teachers]);

  const getFilteredPayments = useMemo(() => {
    const payments: Payment[] = [];
    
    teachers.forEach(teacher => {
      if (teacher.payments) {
        teacher.payments.forEach((payment: any) => {
          payments.push({
            type: 'salary',
            date: payment.paymentDate,
            amount: payment.amount,
            recipient: `${teacher.firstName} ${teacher.lastName}`,
            status: payment.status,
            reference: payment.reference || '-',
            description: `Salary payment for ${teacher.department || 'General'} department`
          });
        });
      }
    });

    students.forEach(student => {
      student.programs.forEach(program => {
        if (program.feeStatus) {
          payments.push({
            type: 'fee',
            date: program.nextPaymentDate || new Date().toISOString(),
            amount: program.fee || 0,
            recipient: student.name,
            status: program.feeStatus,
            reference: program.title || '-',
            description: `Tuition fee for ${program.title}`
          });
        }
      });
    });

    return payments.filter(payment => {
      if (paymentType !== 'all' && payment.type !== paymentType) return false;

      const paymentDate = new Date(payment.date);
      const now = new Date();

      if (dateRange !== 'all') {
        let startDate: Date;
        let endDate: Date = endOfDay(now);

        if (dateRange === 'custom' && customDate) {
          startDate = startOfDay(customDate);
          endDate = endOfDay(customDate);
        } else {
          switch (dateRange) {
            case 'current-month':
              startDate = startOfMonth(now);
              endDate = endOfMonth(now);
              break;
            case 'last-month':
              startDate = startOfMonth(subMonths(now, 1));
              endDate = endOfMonth(subMonths(now, 1));
              break;
            case 'current-year':
              startDate = startOfYear(now);
              endDate = endOfYear(now);
              break;
            case 'last-year':
              startDate = startOfYear(subYears(now, 1));
              endDate = endOfYear(subYears(now, 1));
              break;
            default:
              startDate = subDays(now, parseInt(dateRange));
          }
        }

        if (!isWithinInterval(paymentDate, {
          start: startOfDay(startDate),
          end: endDate
        })) return false;
      }

      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        return (
          payment.recipient.toLowerCase().includes(searchLower) ||
          payment.description.toLowerCase().includes(searchLower) ||
          payment.reference.toLowerCase().includes(searchLower)
        );
      }

      return true;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [teachers, students, paymentType, dateRange, searchQuery, customDate]);

  return {
    stats: calculateRevenueStats,
    filteredPayments: getFilteredPayments
  };
};
